import { useMutation } from "@apollo/client";
import { Step, StepLabel, Stepper } from "@material-ui/core";
import clsx from "clsx";
import { QuitButton } from "components/Back";
import CancellationDialog from "components/CancelDialog/DialogCancellation";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { formatVehiclesSelected, isVehicleTruckChecked, NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { createRequest, UPDATE_REQUEST } from "domains/Request/graphql/mutation";
import { GetClientRequests } from "domains/Request/graphql/queries";
import { ColorlibConnector } from "domains/Task/components/Job/JobLeftSide";
import { AddRequestInput, ERequestStatus, ERequestType, UpdateRequestInput } from "generated/graphql";
import { useCurrentClient } from "hooks";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drawer } from "ui/Drawer";
import { DoubleArrowIcon } from "ui/Icons";
import { Loader } from "ui/Loader";
import { isSuccess } from "utils/https";
import ValidateDialog from "../../SamaPapser/ValidateDialog";
import {
  ContainerTourFormStep,
  FormStep,
  HeaderContainer,
  MainContainer,
  RootTourContainer,
  StepperContainer,
  TitleContainer,
  useTourStyles,
} from "../../shared/MainStyles";
import TourSideDetails from "../../shared/TourSideRecap";
import { Recap } from "./Recap";
import ReccurenceTour from "./ReccurenceTour";
import Stops from "./Stop";

export default function TourForm() {
  const { text } = useText();
  const [activeStep, setActiveStep] = useState(0);
  const styles = useTourStyles();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const navigation = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const steps = [`Stops`, `${text.all.scheduling}`, `Recap`];
  const {
    requestQuoteTour,
    setRequestQuoteTour,
    setTruckType,
    setStep,
    truckType,
    setNextScreen,
    setFieldError,
    isEditedNT,
  } = useContext(NewRequestQuoteContext);
  const dispatchAlert = useAlertDispatch();
  const [addRequestTourForClient] = useMutation(createRequest);
  const { client } = useCurrentClient();
  const [confirm, setConfirm] = useState(false);
  const [updateRequest] = useMutation(UPDATE_REQUEST);

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setRequestQuoteTour(requestQuoteTour);
    }
  };

  useEffect(() => {
    setRequestQuoteTour({ ...requestQuoteTour, type: ERequestType.Tr, templateAxes: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeStep === 2) {
      setStep(3);
    } else {
      setStep(activeStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  const renderStep = () => (
    <Stepper
      alternativeLabel
      activeStep={activeStep}
      connector={<ColorlibConnector />}
      className={styles.stepperStyled}
    >
      {steps.map((label, index) => (
        <Step
          className={clsx({
            activeStep: activeStep === index,
          })}
          key={label}
          onClick={() => null}
        >
          <StepLabel className={styles.stepLabelStyled}>{label}</StepLabel>
        </Step>
      ))}
    </Stepper>
  );
  const addRequestTour = () => {
    setIsLoading(true);
    try {
      const templateToSend = requestQuoteTour.templateAxes.map((axe: any) => {
        const { uid, ...res } = axe;
        return { ...res };
      });
      const request: AddRequestInput = {
        type: requestQuoteTour.type,
        clientId: client?._id,
        productType: requestQuoteTour.productType,
        additionnalInfos: requestQuoteTour.additionnalInfos,
        startDate: requestQuoteTour.startDate,
        endDate: requestQuoteTour.endDate || requestQuoteTour.startDate,
        templateAxes: templateToSend,
        dispatcher: requestQuoteTour?.dispatcher || null,
        handler: requestQuoteTour?.handler || null,
      };
      addRequestTourForClient({
        variables: {
          input: { request },
        },
        refetchQueries: [
          {
            query: GetClientRequests,
            variables: {
              queries: {
                id: client?._id,
                status: [],
              },
            },
          },
        ],
      })
        .then((response) => {
          setIsLoading(false);
          const {
            data: { addRequest },
          } = response;
          if (addRequest?.code === 200) {
            dispatchAlert({
              message: text.requestQuote.successfully_created,
              severity: "success",
            });
            setRequestQuoteTour(null);
            navigation("/tracking/request");
          } else {
            return dispatchAlert({
              message: addRequest?.message || "",
              severity: "error",
            });
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  const handleNext = () => {
    switch (activeStep) {
      case 0:
      case 1:
        setActiveStep(activeStep + 1);
        setNextScreen(true);
        break;
      case 2:
        setConfirm(true);
        break;
      default:
        break;
    }
  };
  const ViewStep = useMemo(() => {
    switch (activeStep) {
      case 0:
        return <Stops onNext={handleNext} />;
      case 1:
        return <ReccurenceTour onNext={handleNext} onBack={handleBack} />;
      case 2:
        return <Recap onNext={() => handleConfirm()} onBack={handleBack} />;
      default:
        return null;
    }
  }, [activeStep]);

  const handleConfirm = () => {
    setConfirm(true);
  };
  const handleOpenModal = () => {
    setOpenCancelDialog(true);
  };
  const handleCloseModal = () => {
    setOpenCancelDialog(false);
  };
  const onConfirmCancel = () => {
    navigation("/");
    setRequestQuoteTour(null);
    setTruckType(null);
  };
  const formatDataTour = () => {
    const axesFormated = requestQuoteTour?.templateAxes.map((axe) => {
      if (isVehicleTruckChecked(axe?.vehicles)) {
        axe.vehicles = formatVehiclesSelected(axe?.vehicles, truckType);
      }
      return {
        name: axe?.name,
        price: parseInt(axe?.price?.toString(), 10),
        discount: axe?.discount ? parseInt(axe?.discount?.toString(), 10) : 0,
        vehicles: axe?.vehicles.map((vehicle) => ({
          vehicleType: vehicle?.vehicleType,
          numberOfVehicule: vehicle?.numberOfVehicule,
        })),
        templateStops: axe?.templateStops.map((stop, index) => ({
          address: {
            address: stop?.address.address,
            place_id: stop?.address.place_id,
          },
        })),
      };
    });
    return {
      templateAxes: axesFormated,
      reccurenceType: requestQuoteTour.reccurenceType,
      reccurenceOptions: requestQuoteTour.reccurenceOptions,
      productType: requestQuoteTour.productType,
      additionnalInfos: requestQuoteTour.additionnalInfos,
      startDate: requestQuoteTour.startDate,
      endDate: requestQuoteTour.endDate || requestQuoteTour.startDate,
      isDeleted: false,
      slotStart: requestQuoteTour.slotStart,
      slotEnd: requestQuoteTour.slotEnd,
      status: ERequestStatus.Requested,
      dispatcher: requestQuoteTour?.dispatcher,
      handler: requestQuoteTour?.handler,
      clientId: client?._id
    };
  };

  const updateRequestByClient = () => {
    setIsLoading(true);
    try {
      const request: UpdateRequestInput = formatDataTour();
      updateRequest({
        variables: {
          input: { id: requestQuoteTour?._id, request },
        },

        refetchQueries: [
          {
            query: GetClientRequests,
            variables: {
              queries: {
                id: client?._id,
                page: 1,
                limit: 9,
                date: "",
                status: [],
              },
            },
          },
        ],
      })
        .then(({ data }) => {
          setIsLoading(false);
          setConfirm(false);
          if (isSuccess(data)) {
            dispatchAlert({
              message: data.results.message,
              severity: "success",
            });
            setRequestQuoteTour(null);
            navigation("/tracking/request");
          } else {
            return dispatchAlert({
              message: data.results.message || "",
              severity: "error",
            });
          }
        })
        .catch(() => {
          setIsLoading(false);
        });
    } catch (error) {
      setIsLoading(false);
    }
  };

  return (
    <MainContainer>
      <RootTourContainer>
        <StepperContainer>
          <HeaderContainer>
            <QuitButton onClick={handleOpenModal} />
            <TitleContainer>{text.all.request_tour_title}</TitleContainer>
            {activeStep !== 2 && (
              <Drawer
                value="right"
                initRightValue={false}
                title={text.requestQuote.request_details}
                name={text.all.details}
                icon={
                  <div className={styles.iconStyleTour}>
                    <DoubleArrowIcon />
                  </div>
                }
                content={<TourSideDetails />}
              />
            )}
          </HeaderContainer>
          {renderStep()}
        </StepperContainer>
        <ContainerTourFormStep>
          <FormStep>{isLoading ? <Loader /> : ViewStep}</FormStep>
        </ContainerTourFormStep>
      </RootTourContainer>
      <CancellationDialog
        title={text.requestQuote.canceling_modal}
        message={text.requestQuote.canceling_confirmation_message}
        open={openCancelDialog}
        onClose={handleCloseModal}
        onConfirm={onConfirmCancel}
      />
      <ValidateDialog
        onClose={() => setConfirm(false)}
        onConfirm={isEditedNT ? updateRequestByClient : addRequestTour}
        open={confirm}
        handleCancel={onConfirmCancel}
      />
    </MainContainer>
  );
}
